/* eslint-disable complexity */
import '../misc/neb-icon';
import { html, css, LitElement } from 'lit';

export const ELEMENTS = {
  container: { id: 'table-container' },
  content: { id: 'table-content' },
  header: { id: 'table-header' },
  rows: { selector: '[id^=row-]' },
  cells: {
    selector: '[id^=cell-]',
    get: (rowIndex, colIndex) => `cell-${rowIndex}-${colIndex}`,
  },
  cellInputs: {
    selector: '[id^=cell-input-]',
    get: (rowIndex, colIndex) => `cell-input-${rowIndex}-${colIndex}`,
  },
};

const DIRECTION = {
  UP: 'up',
  RIGHT: 'right',
  DOWN: 'down',
  LEFT: 'left',
};

const KEY = {
  ENTER: 'Enter',
  TAB: 'Tab',
  ARROW_UP: 'ArrowUp',
  ARROW_DOWN: 'ArrowDown',
  ARROW_LEFT: 'ArrowLeft',
  ARROW_RIGHT: 'ArrowRight',
};

const KEY_DIRECTION = {
  [KEY.ENTER]: DIRECTION.DOWN,
  [KEY.TAB]: DIRECTION.RIGHT,
  [KEY.ARROW_UP]: DIRECTION.UP,
  [KEY.ARROW_DOWN]: DIRECTION.DOWN,
  [KEY.ARROW_LEFT]: DIRECTION.LEFT,
  [KEY.ARROW_RIGHT]: DIRECTION.RIGHT,
};

const isKeyNumber = key => /^[0-9]$/i.test(key);

export default class NebTableSpreadsheet extends LitElement {
  static get properties() {
    return {
      items: { type: Array },
      config: { type: Array },
      disabled: { type: Boolean, reflect: true },

      __visibleItems: { type: Array },
      __editedCells: { type: Object }, // Track which cells have been edited
      __activeCell: { type: Object }, // Track currently active cell
      __scrollPosition: { type: Number },
      __itemHeight: { type: Number },
      __containerHeight: { type: Number },
      __totalHeight: { type: Number },
      __isNavigating: { type: Boolean }, // Flag to track if we're in the middle of navigation
      __lastScrollTime: { type: Number }, // Timestamp of last scroll event
      __scrollThrottle: { type: Number }, // Throttle scroll updates to ~60fps
      __lastStartIndex: { type: Number }, // Last rendered start index for optimization
      __lastVisibleCount: { type: Number }, // Last visible count for optimization
      __valueCache: { type: Object }, // Cache for converted values
    };
  }

  static get styles() {
    return css`
      .table-container {
        position: relative;
        overflow-y: auto;
        border: 1px solid #e1e1e1;
        border-radius: 2px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
          sans-serif;
        font-size: 14px;
      }

      .table-content {
        position: relative;
      }

      .header {
        position: sticky;
        top: 0;
        background: #f8f9fa;
        font-weight: 600;
        border-bottom: 2px solid #ddd;
        display: flex;
        z-index: 1;
        box-shadow: 0 2px 2px rgba(0, 0, 0, 0.05);
      }

      .row {
        display: flex;
        border-bottom: 1px solid #e1e1e1;
        position: absolute;
        width: 100%;
        box-sizing: border-box;
        background: white;
        height: 36px; /* Set explicit height to match __itemHeight */
        overflow: hidden; /* Prevent content from overflowing */
      }

      .row:hover {
        background: #f8f9fa;
      }

      .row:hover .cell.odd {
        background-color: #e6f3ff;
      }

      .row:hover .cell.readonly {
        background-color: #dcdcdc;
      }

      .row:hover .cell.readonly.odd {
        background-color: #a0abad;
      }

      .cell {
        flex: 1;
        padding: 6px 8px;
        border-right: 1px solid #e1e1e1;
        min-width: 100px;
        position: relative;
        cursor: cell;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        box-sizing: border-box; /* Ensure padding is included in width calculation */
      }

      .cell.header-cell {
        background: #f8f9fa;
        cursor: default;
        user-select: none;
      }

      .cell.active {
        outline: 2px solid #1a73e8;
        outline-offset: -2px;
        z-index: 2;
        border: 2px solid #1a73e8;
        padding: 2px 7px 2px 6px; /* Adjust padding to account for the border */
        box-shadow: 0 0 5px rgba(26, 115, 232, 0.3);
      }

      .cell.active.popup {
        padding: 2px 27px 2px 6px; /* Adjust right padding for popup cells with border */
      }

      .cell-input {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: none;
        padding: 6px 8px;
        box-sizing: border-box;
        font-family: inherit;
        font-size: inherit;
        background: white;
      }

      .cell-input:focus {
        outline: none;
      }

      .cell.readonly {
        background-color: #e8e8e8;
        cursor: not-allowed;
      }

      .cell.readonly.odd {
        background-color: #b4bfc1;
      }

      .cell.odd {
        background-color: #f1faff;
      }

      .cell.edited,
      .cell.edited.odd {
        background-color: #e6ffe6;
      }

      /* Remove the actions column styles */
      .actions {
        display: none;
      }

      .cell.popup {
        padding-right: 28px; /* Make room for the expand icon */
      }

      .expand-icon-container {
        position: absolute;
        right: 6px;
        top: 50%;
        transform: translateY(-50%);
        cursor: pointer;
        width: 16px;
        height: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 2px;
        color: #666;
        font-size: 12px;
        z-index: 3;
      }

      .expand-icon-container:hover {
        background-color: #f0f0f0;
        color: #333;
      }

      .expand-icon-container:focus {
        outline: 2px solid #1a73e8;
        outline-offset: 1px;
        background-color: #e3f2fd;
        color: #1a73e8;
      }

      .expand-icon {
        width: 20px;
        height: 20px;
        /* fill: currentColor; */
      }

      .no-items-row {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
      }

      .no-items-content {
        color: #6b7280;
      }

      .no-items-text {
        font-size: 16px;
        font-weight: 500;
        opacity: 0.8;
      }

      /* Disabled state styles */
      :host([disabled]) {
        pointer-events: none;
        opacity: 0.5;
        user-select: none;
      }

      :host([disabled]) .table-container {
        cursor: not-allowed;
      }
    `;
  }

  constructor() {
    super();

    this.__initState();
    this.__initHandlers();
  }

  __initState() {
    this.items = [];
    this.config = [];
    this.disabled = false;

    this.__visibleItems = [];
    this.__editedCells = {};
    this.__activeCell = null;
    this.__scrollPosition = 0;
    this.__itemHeight = 36; // Slightly shorter rows for a denser look
    this.__containerHeight = 400;
    this.__totalHeight = 0;
    this.__isNavigating = false; // Flag to track if we're in the middle of navigation
    this.__lastScrollTime = 0; // Timestamp of last scroll event
    this.__scrollThrottle = 16; // Throttle scroll updates to ~60fps
    this.__lastStartIndex = -1; // Last rendered start index for optimization
    this.__lastVisibleCount = 0; // Last visible count for optimization
    this.__valueCache = {}; // Cache for converted values

    this.onUpdate = () => {};

    this.onOpenPopup = () => {};
  }

  __initHandlers() {
    this.handlers = {
      scroll: e => {
        const now = Date.now();

        // Throttle scroll updates to avoid performance issues
        if (now - this.__lastScrollTime < this.__scrollThrottle) {
          return;
        }

        this.__lastScrollTime = now;
        this.__scrollPosition = e.target.scrollTop;
        this.updateVisibleItems();
      },
      cellClick: (item, field) => {
        if (this.config.find(c => c.key === field)?.editable === false) return;

        this.__activeCell = { id: item.id, field };
        this.requestUpdate();

        // Use requestAnimationFrame to ensure the input is rendered before focusing
        requestAnimationFrame(() => {
          const input = this.shadowRoot.querySelector(
            '.cell.active .cell-input',
          );

          if (input) {
            input.focus();
            input.select(); // Select all text for easy replacement
          }
        });
      },
      cellBlur: (item, field, value) => {
        // If we're navigating, don't process the blur event normally
        // The navigation handlers will take care of it
        if (this.__isNavigating) {
          return;
        }

        this.updateItem(item, field, value);

        this.__activeCell = null;
      },
      keyDown: (e, item, field) => {
        this.handleKeyNavigation(e, item, field);
      },
    };
  }

  resetEditedCells() {
    this.__editedCells = {};
  }

  // Handle numeric input filtering in real-time
  handleNumericInput(e, item, key, allowNegative) {
    const input = e.target;
    const { value } = input;

    const minusCount = (value.match(/-/g) || []).length;
    const numericValue = value.replace(/[^0-9]/g, '') || 0;
    const formattedValue =
      allowNegative && minusCount % 2 === 1
        ? -numericValue
        : Number(numericValue);

    input.value = this.formatNumericValue(key, formattedValue);
  }

  // Handle keyboard navigation
  handleKeyNavigation(e, item, field) {
    const value = e.target?.value;
    const { key } = e;

    let direction = KEY_DIRECTION[key];

    if (key === KEY.TAB && e.shiftKey) direction = DIRECTION.LEFT;

    const configItem = this.config.find(c => c.key === field);
    const isReadOnly = configItem?.getReadOnly?.(item);
    const isForcePopup = configItem?.getForcePopup?.(item);
    const isPopupField = configItem?.popup === true;

    if (direction) {
      if (key === KEY.ENTER && isForcePopup) {
        this.handleExpandClick(e, item, field, isReadOnly);
      }

      if (
        key === KEY.ARROW_RIGHT &&
        value &&
        !isForcePopup &&
        e.target.selectionStart !== value.length
      ) {
        return;
      }

      if (key === KEY.ARROW_LEFT && value && e.target.selectionStart !== 0) {
        return;
      }

      if (key === KEY.ARROW_UP && (e.altKey || e.ctrlKey)) {
        return;
      }

      if (key === KEY.ARROW_DOWN && (e.altKey || e.ctrlKey)) {
        return;
      }

      const skipExpandIcon = key !== KEY.TAB || e.shiftKey || !isPopupField;

      e.preventDefault();
      e.stopPropagation();

      this.handleNavigation(value, item, field, direction, skipExpandIcon);
    } else if (isKeyNumber(key) && isForcePopup) {
      this.handleExpandClick(e, item, field, isReadOnly);
    }
  }

  handleNavigation(value, item, field, direction, skipExpandIcon) {
    this.__isNavigating = true; // Set navigation flag

    const isOnExpandIcon = value === null || value === undefined;

    if (isOnExpandIcon) {
      if (direction === DIRECTION.LEFT) {
        this.navigateBackToSameCell(item, field);
      } else {
        this.navigateToNextCell(item, field, direction);
      }

      return;
    }

    this.updateItem(item, field, value);

    if (!skipExpandIcon) {
      this.navigateToExpandIcon(item, field, direction);
    } else {
      // Navigate to the next cell normally
      this.navigateToNextCell(item, field, direction);
    }
  }

  // Convert cents to dollars with decimal point
  centsToDollars(value) {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    // Use cache for repeated values
    const cacheKey = `c2d_${value}`;

    if (this.__valueCache[cacheKey]) {
      return this.__valueCache[cacheKey];
    }

    // Convert to number if it's a string
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;

    // Check if it's a valid number
    if (Number.isNaN(numValue)) {
      return '';
    }

    // Convert cents to dollars with 2 decimal places
    const result = (numValue / 100).toFixed(2);

    // Cache the result
    this.__valueCache[cacheKey] = result;

    return result;
  }

  // Get the actual value for a cell, including calculated values
  getCellValue(item, field) {
    const configItem = this.config.find(c => c.key === field);

    // If the config has a calculate function, use it to compute the value
    if (configItem?.calculate) {
      return configItem.calculate(item);
    }

    // Otherwise, return the item's property value
    return item[field];
  }

  // Format numeric values for display
  formatNumericValue(field, value) {
    // Check if this is a numeric column that needs cents-to-dollars conversion
    if (this.config.find(c => c.key === field)?.numeric) {
      const dollarValue = this.centsToDollars(value);
      return dollarValue ? `$${dollarValue}` : '';
    }

    // Return original value if not a numeric field or invalid number
    return value;
  }

  // Process edited values before sending to parent
  processEditedValue(item, field, value) {
    // For numeric columns, we need to convert the user input (in dollars) to cents for storage
    if (this.config.find(c => c.key === field)?.numeric) {
      // Remove any non-numeric characters except decimal point
      const cleanValue = value.toString().replace(/[^\d.-]/g, '');

      // Parse as float
      const floatValue = parseFloat(cleanValue);

      // Check if it's a valid number
      if (!Number.isNaN(floatValue)) {
        // Convert dollars to cents (round to avoid floating point issues)
        // User input is already in dollars, so multiply by 100 to get cents
        return Math.round(floatValue * 100);
      }
      return 0; // Default to 0 if invalid
    }
    return value;
  }

  updateItem(item, field, value) {
    const processedValue = this.processEditedValue(item, field, value);

    // Only update if the value has actually changed
    let valueChanged = false;

    if (processedValue !== item[field]) {
      // Update the edited cells tracking
      if (!this.__editedCells[item.id]) {
        this.__editedCells[item.id] = {};
      }
      this.__editedCells[item.id][field] = processedValue;
      valueChanged = true;
    }

    // Only dispatch event if values actually changed
    if (valueChanged) {
      const updatedItem = {
        ...item,
        [field]: processedValue,
      };

      this.onUpdate(updatedItem, field, value);
    }
  }

  firstUpdated() {
    const container = this.shadowRoot.querySelector('.table-container');

    if (container) {
      this.__containerHeight = container.clientHeight;

      // Add a keydown event listener to the container to capture Tab key
      container.addEventListener(
        'keydown',
        this.handleContainerKeyDown.bind(this),
      );
    }
  }

  // Handle keydown events at the container level to ensure Tab is captured
  handleContainerKeyDown(e) {
    if (e.key === KEY.TAB) {
      // Prevent default Tab behavior to keep focus within the table
      e.preventDefault();
    }
  }

  updateVisibleItems() {
    const startIndex = Math.floor(this.__scrollPosition / this.__itemHeight);
    const visibleCount =
      Math.ceil(this.__containerHeight / this.__itemHeight) + 2; // Add buffer rows

    // Skip update if visible range hasn't changed
    if (
      startIndex === this.__lastStartIndex &&
      visibleCount === this.__lastVisibleCount
    ) {
      return;
    }

    this.__lastStartIndex = startIndex;
    this.__lastVisibleCount = visibleCount;

    const endIndex = Math.min(startIndex + visibleCount, this.items.length);

    // Reuse existing array if possible to reduce GC pressure
    this.__visibleItems.length = 0;

    for (let i = startIndex; i < endIndex; i++) {
      const item = this.items[i];
      this.__visibleItems.push({
        ...item,
        isOdd: i % 2 === 1,
        position: i * this.__itemHeight,
      });
    }

    this.requestUpdate();
  }

  updated(changedProperties) {
    if (changedProperties.has('items') && this.items) {
      this.__totalHeight = this.items.length * this.__itemHeight;
      this.__lastStartIndex = -1; // Reset to force update
      this.updateVisibleItems();
    }
  }

  renderHeader() {
    if (!this.items.length) return null;

    return html`
      <div class="header" id="${ELEMENTS.header.id}">
        ${this.config.map(config => {
          const isPopup = config.popup === true;
          const headerClass = `cell header-cell ${isPopup ? 'popup' : ''}`;
          return html`<div class="${headerClass}">${config.label}</div>`;
        })}
      </div>
    `;
  }

  handleCellClick(item, key, isReadOnly) {
    if (isReadOnly || this.disabled) return;

    this.handlers.cellClick(item, key);
    // Use requestAnimationFrame to ensure the input is rendered before focusing
    requestAnimationFrame(() => {
      const input = this.shadowRoot.querySelector('.cell.active .cell-input');

      if (input) {
        input.focus();
        input.select(); // This will also select all text in the cell
      }
    });
  }

  renderCellInput(
    item,
    key,
    value,
    rowIndex,
    colIndex,
    isForcePopup,
    isReadOnly,
    allowNegative,
  ) {
    // Use requestAnimationFrame to ensure the input is focused after rendering
    requestAnimationFrame(() => {
      const input = this.shadowRoot.querySelector('.cell.active .cell-input');

      if (input) {
        input.focus();
        input.select(); // Select all text for easy replacement
      }
    });

    return html`
      <input
        id="${ELEMENTS.cellInputs.get(rowIndex, colIndex)}"
        class="cell-input"
        .value="${value}"
        @keydown="${e => this.handlers.keyDown(e, item, key)}"
        @input="${e => this.handleNumericInput(e, item, key, allowNegative)}"
        @blur="${e => this.handlers.cellBlur(item, key, e.target.value)}"
        @click="${e => e.stopPropagation()}"
        ?readonly="${isForcePopup || isReadOnly}"
      />
    `;
  }

  getCellClassNames(isEditing, isEdited, isReadOnly, isOdd, isPopup) {
    return `cell ${isEdited ? 'edited' : ''} ${isEditing ? 'active' : ''} ${isReadOnly ? 'readonly' : ''} ${isOdd ? 'odd' : ''} ${isPopup ? 'popup' : ''}`;
  }

  // eslint-disable-next-line complexity
  renderCell(item, key, index) {
    const isEditing =
      this.__activeCell &&
      this.__activeCell.id === item.id &&
      this.__activeCell.field === key;

    const isEdited =
      this.__editedCells[item.id] &&
      this.__editedCells[item.id][key] !== undefined;

    const configItem = this.config.find(c => c.key === key);
    const isReadOnly =
      configItem?.editable === false || configItem?.getReadOnly?.(item);
    const isPopup = configItem?.popup === true;
    const isForcePopup = configItem?.getForcePopup?.(item);
    const allowNegative = configItem?.allowNegative === true;
    // Use getCellValue to get the actual value (including calculated values)
    const actualValue = this.getCellValue(item, key);
    const displayValue = configItem?.numeric
      ? this.formatNumericValue(key, actualValue)
      : actualValue;

    const classNames = this.getCellClassNames(
      isEditing,
      isEdited,
      isReadOnly,
      item.isOdd,
      isPopup,
    );

    // Get the column index from the config
    const colIndex = this.config.findIndex(c => c.key === key);

    return html`
      <div
        id="${ELEMENTS.cells.get(index, colIndex)}"
        class="${classNames}"
        title="${displayValue}"
        @click="${e => {
          if (isForcePopup) {
            this.handleExpandClick(e, item, key, isReadOnly);
          } else {
            this.handleCellClick(item, key, isReadOnly);
          }
        }}"
      >
        ${isEditing
          ? this.renderCellInput(
              item,
              key,
              displayValue,
              index,
              colIndex,
              isForcePopup,
              isReadOnly,
              allowNegative,
            )
          : displayValue}
        ${isPopup && !isReadOnly
          ? html`
              <div
                class="expand-icon-container"
                tabindex="0"
                @click="${e =>
                  this.handleExpandClick(e, item, key, isReadOnly)}"
                @keydown="${e =>
                  this.handleExpandIconKeydown(e, item, key, isReadOnly)}"
                title="Open popup"
              >
                <neb-icon class="expand-icon" icon="neb:expand" />
              </div>
            `
          : ''}
      </div>
    `;
  }

  renderRow(item, position, index) {
    return html`
      <div
        id="row-${index}"
        class="row"
        style="transform: translateY(${position}px)"
      >
        ${this.config.map(configItem =>
          this.renderCell(item, configItem.key, index),
        )}
      </div>
    `;
  }

  render() {
    return html`
      <div
        class="table-container"
        id="${ELEMENTS.container.id}"
        style="height: ${this.__containerHeight}px"
        @scroll=${this.handlers.scroll}
      >
        ${this.__visibleItems.length === 0
          ? html`
              <div
                class="no-items-row"
                style="height: ${this.__containerHeight}px;"
              >
                <div class="no-items-content">
                  <div class="no-items-text">No items to display</div>
                </div>
              </div>
            `
          : html`
              ${this.renderHeader()}
              <div
                class="table-content"
                id="${ELEMENTS.content.id}"
                style="height: ${this.__totalHeight}px"
              >
                ${this.__visibleItems.map((item, index) =>
                  this.renderRow(item, item.position, index),
                )}
              </div>
            `}
      </div>
    `;
  }

  navigateBackToSameCell(item, field) {
    const { currentItemIndex, currentFieldIndex } = this.findCurrentPosition(
      item,
      field,
    );

    this.applyNavigation(this.items, currentItemIndex, currentFieldIndex);
  }

  navigateToExpandIcon(item, field, direction) {
    // Tab from popup cell input should go to expand icon
    this.__activeCell = null; // Clear active cell
    this.requestUpdate();

    // Focus the expand icon after render
    requestAnimationFrame(() => {
      const rowIndex = this.__visibleItems.findIndex(i => i.id === item.id);
      const colIndex = this.config.findIndex(c => c.key === field);
      const expandIcon = this.shadowRoot.querySelector(
        `#${ELEMENTS.cells.get(rowIndex, colIndex)} .expand-icon-container`,
      );

      if (expandIcon) {
        expandIcon.focus();
        this.__isNavigating = false;
      } else {
        // Fallback to normal navigation if expand icon not found
        this.navigateToNextCell(item, field, direction);
      }
    });
  }

  // Navigate to the next cell in the specified direction
  navigateToNextCell(currentItem, currentField, direction) {
    // Get all visible items and validate
    const allItems = this.items;
    if (!allItems.length) return;

    // Find current positions
    const { currentItemIndex, currentFieldIndex } = this.findCurrentPosition(
      currentItem,
      currentField,
    );

    if (currentItemIndex === -1 || currentFieldIndex === -1) {
      console.error('Could not find current position');
      this.__isNavigating = false; // Reset navigation flag
      return;
    }

    const colIndex = this.config.findIndex(c => c.key === currentField);

    // Calculate next position based on direction
    const { nextItemIndex, nextFieldIndex } = this.calculateNextPosition(
      currentItemIndex,
      colIndex,
      allItems.length,
      direction,
    );

    // Apply navigation to move to the next cell
    this.applyNavigation(allItems, nextItemIndex, nextFieldIndex);
  }

  // Find the current position in the grid
  findCurrentPosition(currentItem, currentField) {
    const allItems = this.items;
    const currentItemIndex = allItems.findIndex(
      item => item.id === currentItem.id,
    );

    const currentFieldIndex = this.config.findIndex(
      c => c.key === currentField,
    );

    return { currentItemIndex, currentFieldIndex };
  }

  // Calculate the next position based on direction
  calculateNextPosition(currentItemIndex, colIndex, itemsLength, direction) {
    const isCellNotNavigatable = (fieldIndex, itemIndex) =>
      !this.config[fieldIndex]?.editable ||
      this.config[fieldIndex]?.getReadOnly?.(this.items[itemIndex]);

    let nextItemIndex = currentItemIndex;
    let nextFieldIndex = colIndex;

    switch (direction) {
      case DIRECTION.RIGHT: {
        do {
          nextFieldIndex = (nextFieldIndex + 1) % this.config.length;

          if (nextFieldIndex === 0) {
            nextItemIndex = (currentItemIndex + 1) % itemsLength;
          }
        } while (isCellNotNavigatable(nextFieldIndex, nextItemIndex));

        break;
      }

      case DIRECTION.LEFT: {
        do {
          nextFieldIndex =
            (nextFieldIndex - 1 + this.config.length) % this.config.length;

          if (nextFieldIndex === this.config.length - 1) {
            nextItemIndex = (currentItemIndex - 1 + itemsLength) % itemsLength;
          }
        } while (isCellNotNavigatable(nextFieldIndex, nextItemIndex));

        break;
      }

      case DIRECTION.DOWN: {
        do {
          nextItemIndex = (nextItemIndex + 1) % itemsLength; // Wrap to first row
        } while (isCellNotNavigatable(nextFieldIndex, nextItemIndex));

        break;
      }

      case DIRECTION.UP: {
        do {
          nextItemIndex = (nextItemIndex - 1 + itemsLength) % itemsLength;
        } while (isCellNotNavigatable(nextFieldIndex, nextItemIndex));

        break;
      }

      default:
    }

    return { nextItemIndex, nextFieldIndex };
  }

  // Apply the navigation to the new cell
  applyNavigation(allItems, itemIndex, fieldIndex) {
    // Get the next item and field
    const nextItem = allItems[itemIndex];
    const nextField = this.config[fieldIndex].key;

    // Set the active cell and scroll if needed
    this.__activeCell = { id: nextItem.id, field: nextField };
    this.requestUpdate();

    // Ensure the next cell is visible by scrolling if necessary
    this.ensureCellIsVisible(itemIndex);

    // Focus the input after the update
    requestAnimationFrame(() => {
      const input = this.shadowRoot.querySelector('.cell.active .cell-input');

      if (input) {
        input.focus();
        input.select();
        // Reset the navigation flag after focus is set
        this.__isNavigating = false;
      } else {
        // If we couldn't find the input, reset the flag anyway
        this.__isNavigating = false;
      }
    });
  }

  // Ensure the cell at the given index is visible by scrolling if necessary
  ensureCellIsVisible(itemIndex) {
    const itemPosition = itemIndex * this.__itemHeight;
    const container = this.shadowRoot.querySelector('.table-container');

    if (!container) return;

    const containerHeight = container.clientHeight;
    const { scrollTop } = container;

    // If the item is above the visible area, scroll up
    if (itemPosition < scrollTop) {
      container.scrollTop = itemPosition;
    }

    // If the item is below the visible area, scroll down
    else if (
      itemPosition >
      scrollTop + containerHeight - this.__itemHeight * 2
    ) {
      container.scrollTop =
        itemPosition - containerHeight + this.__itemHeight * 2;
    }
  }

  async handleExpandClick(e, item, field, isReadOnly) {
    if (isReadOnly || this.disabled) return;

    e.stopPropagation(); // Prevent cell click event

    // Store the current active cell state
    const wasActive =
      this.__activeCell &&
      this.__activeCell.id === item.id &&
      this.__activeCell.field === field;

    // If not already active, make it active
    if (!wasActive) {
      this.__activeCell = { id: item.id, field };
      this.requestUpdate();
    }

    const changedValue = await this.onOpenPopup(item, field);

    if (changedValue !== false) {
      if (!this.__editedCells[item.id]) {
        this.__editedCells[item.id] = {};
      }

      this.__editedCells[item.id][field] = changedValue;
    }

    // Restore active state and focus after popup closes
    this.__activeCell = { id: item.id, field };
    this.requestUpdate();

    // Focus the cell input after the update
    requestAnimationFrame(() => {
      const input = this.shadowRoot.querySelector('.cell.active .cell-input');

      if (input) {
        input.focus();
        input.select();
      }
    });
  }

  handleExpandIconKeydown(e, item, field, isReadOnly) {
    if (e.key === KEY.ENTER || e.key === ' ') {
      e.preventDefault();
      this.handleExpandClick(e, item, field, isReadOnly);
    } else {
      this.handleKeyNavigation(e, item, field);
    }
  }
}

customElements.define('neb-table-spreadsheet', NebTableSpreadsheet);
